// 付箋・テキストオブジェクトの「編集中も即時 WYSIWYG（マーカー除去）」を実現する共有ロジック。
//
// 方針（設計 plan.md v3）:
// - 保存は生 Markdown のまま。編集領域 DOM は「生 Markdown の 1 行 = 1 ブロック <div>」の正準構造とし、
//   記法マーカーは data 属性の隠しプレフィックス（data-prefix）へ、箇条書きグリフは ::before（styles.css）へ
//   逃がす。本文だけをテキストノードに置くことで、キャレット/本文オフセット計算を単純化する。
// - data-prefix に元の生プレフィックスをそのまま保持するため build↔serialize が生テキストを
//   （LF 正規化のもとで）完全ラウンドトリップする（`*`/`+` や字下げをユーザー入力どおり保存）。
// - Enter / 削除 / インデント等の構造変更は「生テキスト + キャレット/選択の純粋関数」として実装し、
//   DOM 非依存で単体テスト可能にする。呼び出し側（use-object-text-editor.ts）は beforeinput で
//   構造変更を横取りし、純粋変換 → 正準ブロック再構築 → キャレット復元、の順で適用する。
// - innerHTML / dangerouslySetInnerHTML は使わず createElement + textContent のみで構築する
//   （ユーザー入力が HTML 解釈される余地を構造的に排除。markdown-text.ts の buildMarkdownTextContent と同方針）。

import { splitTextIntoLinkSegments, type LinkSegment } from "./linkify-text";
import {
  markdownLineStyleFor,
  parseMarkdownLineParts,
  type HeadingLevel,
  type MarkdownLineParts,
  type MarkdownTextLine,
} from "./markdown-text";

/** 箇条書きブロックに付与する class（styles.css でマーカー span のスタイルを当てる）。 */
export const EDITOR_BULLET_CLASS = "rc-md-editor-bullet";
/** 箇条書きマーカー（•/◦/▪）を描画する編集不可 span の class。本文（テキストノード）とは区別する。 */
export const EDITOR_MARKER_CLASS = "rc-md-marker";

/** 生テキスト + キャレット位置（生テキスト先頭からの文字オフセット）。純粋変換の戻り値。 */
export interface EditorTextAndCaret {
  readonly text: string;
  readonly caret: number;
}

/** 生テキスト上の選択範囲（start<=end。collapsed のとき start===end）。 */
export interface EditorSelection {
  readonly start: number;
  readonly end: number;
}

/** リモートキャレット描画（canvas-remote-carets-layer）向けの表示 DOM 位置。 */
export interface DisplayCaretPosition {
  /** 行（先頭から 0 始まり）。表示 DOM の行 <div> の index に対応。 */
  readonly lineIndex: number;
  /** その行の表示テキスト内での文字オフセット。 */
  readonly lineOffset: number;
}

/** 受信側 DOM の種別。editor=WYSIWYG 編集 DOM（本文のみ）、view=MarkdownTextView（グリフは実テキスト）。 */
export type CaretSurface = "editor" | "view";

//------------------------------------------------------------------------------
// DOM 構築 / シリアライズ
//------------------------------------------------------------------------------

/**
 * 生 Markdown から正準ブロック DOM を構築して `el` の子として設定する。
 * マーカーは data-prefix（隠し）へ、箇条書きグリフは ::before（class + data-md-glyph）へ逃がし、
 * 本文だけをテキストノードに置く。空本文（空の箇条書き）は空テキストノードを常設して Range 復元先を保証する。
 */
export function buildMarkdownEditorDom(el: HTMLElement, rawText: string): void {
  const doc = el.ownerDocument;
  el.replaceChildren();
  // parseMarkdownLineParts は CRLF/LF いずれでも動くが、行分割は既存パーサと同じ /\r?\n/ に合わせる。
  const lines = rawText.split(/\r?\n/);
  for (const line of lines) {
    const parts = parseMarkdownLineParts(line);
    const div = doc.createElement("div");
    div.dataset.md = parts.kind;
    if (parts.kind === "heading" || parts.kind === "bullet") {
      div.dataset.level = String(parts.level);
    }
    // 生プレフィックスをそのまま保持する（serialize での完全復元とキャレット座標系の基準）。
    div.dataset.prefix = parts.rawPrefix;
    applyLineStyle(div, parts);
    if (parts.kind === "bullet") {
      div.classList.add(EDITOR_BULLET_CLASS);
      // マーカー（•/◦/▪）は編集不可の span として本文の手前に置く。本文はこの span の直後の
      // テキストノードに入るため、キャレットは常にマーカーの右（本文先頭）に来て、本文が空でも
      // マーカーへ重ならない。serialize は本文テキストノードだけを読み、この span は除外する。
      const marker = doc.createElement("span");
      marker.className = EDITOR_MARKER_CLASS;
      marker.setAttribute("contenteditable", "false");
      marker.setAttribute("aria-hidden", "true");
      marker.textContent = `${parts.glyph} `;
      div.appendChild(marker);
    }
    if (parts.kind === "blank") {
      // 空行は <br> で 1 行分の高さを維持する（空 div は高さ 0 になるため）。
      div.appendChild(doc.createElement("br"));
    } else {
      // 空本文でも Range を張れるよう、空文字列でもテキストノードを常設する。
      div.appendChild(doc.createTextNode(parts.content));
    }
    el.appendChild(div);
  }
}

/**
 * 正準ブロック DOM から生 Markdown を復元する。各直下ブロックの `data-prefix + textContent` を
 * `\n` で連結する（::before グリフは textContent に含まれないため本文だけが取れる）。
 * 前提: 構造を壊す入力（改行・削除・貼付・範囲置換）は呼び出し側の beforeinput 制御で横取り済みで、
 * DOM は常に正準ブロック構造であること。
 */
export function serializeMarkdownEditorDom(el: HTMLElement): string {
  const lines: string[] = [];
  // ブラウザが正準ブロックの外へ差し込んだ裸テキストノード（空エディタへの初回入力などで起こり得る）を
  // 取りこぼさないよう、childNodes を走査して直近ブロックの本文へ寄せる。次の onInput 再構築で正準化される。
  let pendingStray = "";
  for (const node of Array.from(el.childNodes)) {
    if (node instanceof HTMLElement && node.dataset.md !== undefined) {
      const prefix = trustedBlockPrefix(node);
      // 本文は直下のテキストノードのみ（マーカー span 等の要素は除外する）。
      lines.push(pendingStray + prefix + blockContentText(node));
      pendingStray = "";
    } else {
      pendingStray += node.textContent ?? "";
    }
  }
  if (pendingStray.length > 0) {
    if (lines.length > 0) lines[lines.length - 1] += pendingStray;
    else lines.push(pendingStray);
  }
  return lines.join("\n");
}

//------------------------------------------------------------------------------
// 選択 / キャレットの保存・復元（生テキストのフラットオフセット座標系）
//------------------------------------------------------------------------------

/**
 * 現在の選択範囲を生テキストオフセットで返す。フォーカスが `el` 外なら null。
 * collapsed（キャレットのみ）のときは start===end。
 */
export function saveEditorSelection(el: HTMLElement): EditorSelection | null {
  const view = el.ownerDocument.defaultView;
  const selection = view?.getSelection?.() ?? null;
  if (!selection || selection.rangeCount === 0) return null;
  return rawSelectionFromRange(el, selection.getRangeAt(0));
}

/** collapsed キャレットの生テキストオフセットを返す（saveEditorSelection の start 相当）。 */
export function saveEditorCaretOffset(el: HTMLElement): number | null {
  return saveEditorSelection(el)?.start ?? null;
}

/**
 * Range（beforeinput の getTargetRanges 由来の StaticRange 等）を生テキスト選択範囲へ変換する。
 * オートコレクト（insertReplacementText）のように「見た目の Selection は collapsed でも実際の対象は語」の
 * ケースがあるため、変換対象は Selection ではなく対象 Range から求める必要がある。
 */
export function rawSelectionFromRange(
  el: HTMLElement,
  range: { startContainer: Node; startOffset: number; endContainer: Node; endOffset: number }
): EditorSelection | null {
  if (!el.contains(range.startContainer) || !el.contains(range.endContainer)) return null;
  const start = rawOffsetOfPoint(el, range.startContainer, range.startOffset);
  const end = rawOffsetOfPoint(el, range.endContainer, range.endOffset);
  if (start === null || end === null) return null;
  return start <= end ? { end, start } : { end: start, start: end };
}

/**
 * 生テキスト `raw` に対して現在の DOM（正準ブロック）を作り直す必要があるかを返す。
 * 行数・各行の種別（data-md）・生プレフィックス（data-prefix）のいずれかが変われば再構築が必要。
 * 本文内の通常タイプ（種別・プレフィックス不変）では false を返し、無駄な再構築とキャレット揺れを避ける。
 */
export function editorNeedsRebuild(el: HTMLElement, raw: string): boolean {
  // 正準ブロック（data-md 付き）以外の子（裸テキスト・ブラウザ挿入の要素）があれば必ず作り直す。
  for (const node of Array.from(el.childNodes)) {
    if (!(node instanceof HTMLElement) || node.dataset.md === undefined) return true;
  }
  const lines = raw.split(/\r?\n/);
  const blocks = Array.from(el.children).filter((c): c is HTMLElement => c instanceof HTMLElement);
  if (lines.length !== blocks.length) return true;
  for (let i = 0; i < lines.length; i += 1) {
    const parts = parseMarkdownLineParts(lines[i] ?? "");
    const block = blocks[i];
    if (!block) return true;
    if (block.dataset.md !== parts.kind) return true;
    if ((block.dataset.prefix ?? "") !== parts.rawPrefix) return true;
    // bullet はマーカー span を必ず持つのが正準（ネイティブのブロック分割クローンは span を欠く）。
    // serialize 出力の変化からの間接検知だけだと、本文が bullet 記法で始まるケースで kind/prefix が
    // 偶然一致して非正準状態が恒久化するため、span の欠落を直接検知して再構築する。
    if (parts.kind === "bullet" && block.querySelector(`.${EDITOR_MARKER_CLASS}`) === null) {
      return true;
    }
    // 本文は単一テキストノードが正準。IME 確定直後はブラウザが確定テキストを別ノードとして挿入し
    // 複数テキストノード（空ノード + 確定ノード）になり得るため、検知して正準へ作り直す
    // （キャレットは rawOffsetOfPoint が全ノード連結基準で正しく保存→復元できる）。
    if (directTextNodeCount(block) > 1) return true;
    // アンカーの健全性: blank は <br>、それ以外は本文テキストノードを必ず持つ（ネイティブ削除で
    // <br> やテキストノードが消えた場合に正準へ作り直せるよう検出する）。
    if (parts.kind === "blank") {
      if (block.querySelector("br") === null) return true;
    } else if (firstTextNode(block) === null) {
      return true;
    }
  }
  return false;
}

/**
 * 生テキストオフセット `offset` の位置へ collapsed キャレットを復元する。
 * 該当ブロックを走査で特定し、本文オフセット = clamp(offset − 行頭 − prefix長, 0, 本文長) の位置へ Range を張る。
 * 隠しプレフィックス領域に落ちた場合は本文先頭へクランプする。
 * 前提: 復元先は buildMarkdownEditorDom 直後の正準 DOM（本文 = 単一テキストノード）に限る。
 * 保存側（rawOffsetOfPoint）は複数テキストノードにも対応するが、復元側は firstTextNode 基準のままとする。
 */
export function restoreEditorCaretOffset(el: HTMLElement, offset: number): void {
  const doc = el.ownerDocument;
  const view = doc.defaultView;
  const selection = view?.getSelection?.() ?? null;
  if (!selection) return;
  const blocks = Array.from(el.children).filter((c): c is HTMLElement => c instanceof HTMLElement);
  const range = doc.createRange();
  let base = 0;
  for (let i = 0; i < blocks.length; i += 1) {
    const block = blocks[i];
    if (!block) continue;
    const prefixLen = (block.dataset.prefix ?? "").length;
    const content = firstTextNode(block);
    const contentLen = content?.textContent?.length ?? 0;
    const lineLen = prefixLen + contentLen;
    const isLast = i === blocks.length - 1;
    // この行の範囲（行頭 base 〜 行末 base+lineLen）に収まるか。最終行は超過分もこの行へ寄せる。
    if (offset <= base + lineLen || isLast) {
      const contentOffset = clamp(offset - base - prefixLen, 0, contentLen);
      if (content) {
        range.setStart(content, contentOffset);
      } else {
        // blank 行（<br> のみ）などテキストノードが無い場合はブロック先頭へ。
        range.setStart(block, 0);
      }
      range.collapse(true);
      selection.removeAllRanges();
      selection.addRange(range);
      return;
    }
    base += lineLen + 1; // 改行 1 文字ぶん進める。
  }
}

//------------------------------------------------------------------------------
// 純粋変換（生テキスト + 選択 → 生テキスト + キャレット）
//------------------------------------------------------------------------------

/**
 * `[start, end)` を `insert`（CRLF は LF 正規化）で置換する。貼り付け・ドロップ・選択上書き入力に使う。
 * キャレットは挿入後の末尾。
 */
export function insertTextAt(
  text: string,
  start: number,
  end: number,
  insert: string
): EditorTextAndCaret {
  const [lo, hi] = orderedBounds(text, start, end);
  const normalized = insert.replace(/\r\n?/g, "\n");
  return { caret: lo + normalized.length, text: text.slice(0, lo) + normalized + text.slice(hi) };
}

/**
 * Enter（改行）。要望②のリスト継続:
 * - 本文のある箇条書き行 → 同じ生プレフィックスを複製して次行も箇条書きにする（`*`/`+`・字下げを保存）。
 * - 空の箇条書き行 → マーカーを外して plain 空行にする（リスト終了・行は増やさない）。
 * - それ以外（見出し/plain） → 通常改行を挿入（見出しを分割した後半は plain）。
 * 範囲選択があれば先に削除してから判定する。
 */
export function splitForEnter(text: string, start: number, end: number): EditorTextAndCaret {
  const [lo, hi] = orderedBounds(text, start, end);
  const base = text.slice(0, lo) + text.slice(hi);
  const pos = lo;
  const { lineStart, lineEnd, lineText } = lineBoundsAt(base, pos);
  const parts = parseMarkdownLineParts(lineText);
  if (parts.kind === "bullet") {
    if (parts.content.length === 0) {
      // 空箇条書き → マーカー除去で plain 空行化（行頭へ）。
      return {
        caret: lineStart,
        text: base.slice(0, lineStart) + base.slice(lineEnd),
      };
    }
    // 本文あり → 改行 + 同一プレフィックスで箇条書き継続。
    const inserted = `\n${parts.rawPrefix}`;
    return { caret: pos + inserted.length, text: base.slice(0, pos) + inserted + base.slice(pos) };
  }
  // 見出し/plain/blank は通常改行。
  return { caret: pos + 1, text: `${base.slice(0, pos)}\n${base.slice(pos)}` };
}

/** Shift+Enter。常に plain 改行を挿入する（リスト継続・終了はしない）。 */
export function insertPlainLineBreak(text: string, start: number, end: number): EditorTextAndCaret {
  return insertTextAt(text, start, end, "\n");
}

/**
 * Tab / Shift+Tab による箇条書きのネスト変更（delta = +1 / -1）。
 * キャレット行が箇条書きでなければ null（呼び出し側は preventDefault のみで no-op）。
 * 選択が複数行にまたがる場合も開始位置（start）の行のみを対象にする（複数行一括インデントは非対応）。
 */
export function changeBulletIndent(
  text: string,
  start: number,
  _end: number,
  delta: number,
  maxLevel: number
): EditorTextAndCaret | null {
  const { lineStart, lineEnd, lineText } = lineBoundsAt(text, start);
  const parts = parseMarkdownLineParts(lineText);
  if (parts.kind !== "bullet") return null;
  const newLevel = clamp(parts.level + delta, 0, maxLevel);
  const marker = parts.rawPrefix.trimStart().charAt(0) || "-";
  const newPrefix = `${"  ".repeat(newLevel)}${marker} `;
  const newLine = newPrefix + parts.content;
  const newText = text.slice(0, lineStart) + newLine + text.slice(lineEnd);
  // キャレットは本文内の同じ位置に保つ（prefix 長の増減ぶんシフト）。
  const contentOffset = Math.max(0, start - lineStart - parts.rawPrefix.length);
  return { caret: lineStart + newPrefix.length + contentOffset, text: newText };
}

/**
 * Backspace の構造変換。呼び出し側は「本文先頭（境界）でのみ」本関数を使う（境界判定は isBackspaceBoundary）。
 * - 箇条書き本文先頭: level>0 は 1 段浅く、level==0 は plain（マーカー除去）。
 * - 見出し本文先頭: plain（`#` 除去）。
 * - plain/blank 行頭（前行あり）: 前行と結合。
 * 境界以外では単純な 1 文字削除（安全側のフォールバック）。
 */
export function deleteBackward(text: string, caret: number, maxLevel: number): EditorTextAndCaret {
  if (caret <= 0) return { caret: 0, text };
  const { lineStart, lineEnd, lineText } = lineBoundsAt(text, caret);
  const parts = parseMarkdownLineParts(lineText);
  const contentStart = lineStart + parts.rawPrefix.length;
  if (caret === contentStart && parts.kind === "bullet") {
    if (parts.level > 0) {
      const outdented = changeBulletIndent(text, caret, caret, -1, maxLevel);
      if (outdented) return outdented;
    }
    // level 0 → plain 化（マーカー除去）。
    return {
      caret: lineStart,
      text: text.slice(0, lineStart) + parts.content + text.slice(lineEnd),
    };
  }
  if (caret === contentStart && parts.kind === "heading") {
    return {
      caret: lineStart,
      text: text.slice(0, lineStart) + parts.content + text.slice(lineEnd),
    };
  }
  if (caret === lineStart && lineStart > 0) {
    // 行頭で前行と結合。吸収される側（本行）の生プレフィックスは落とし、本文だけを前行末尾へ繋ぐ
    // （マーカー文字が本文へ漏れないようにする）。
    const bounds = lineBoundsAt(text, caret);
    const parts = parseMarkdownLineParts(bounds.lineText);
    const prevEnd = lineStart - 1; // 前行末尾（"\n" の位置）
    return {
      caret: prevEnd,
      text: text.slice(0, prevEnd) + parts.content + text.slice(bounds.lineEnd),
    };
  }
  // フォールバック: 本文内 1 文字削除。
  return { caret: caret - 1, text: text.slice(0, caret - 1) + text.slice(caret) };
}

/**
 * Delete（前方削除）の構造変換。呼び出し側は「行末（境界）でのみ」使う（判定は isDeleteForwardBoundary）。
 * 行末で次行があれば結合、それ以外は 1 文字前方削除（フォールバック）。
 */
export function deleteForward(text: string, caret: number): EditorTextAndCaret {
  if (caret >= text.length) return { caret: text.length, text };
  const { lineEnd } = lineBoundsAt(text, caret);
  if (caret === lineEnd && lineEnd < text.length) {
    // 行末で次行と結合。吸収される次行の生プレフィックス（マーカー）は落とし、本文だけを本行末尾へ繋ぐ
    // （`- a` の行末で Delete したとき次行 `- b` の "- " が本文へ漏れて "- a- b" になるのを防ぐ）。
    const nextBounds = lineBoundsAt(text, lineEnd + 1);
    const nextParts = parseMarkdownLineParts(nextBounds.lineText);
    return {
      caret,
      text: text.slice(0, lineEnd) + nextParts.content + text.slice(nextBounds.lineEnd),
    };
  }
  return { caret, text: text.slice(0, caret) + text.slice(caret + 1) };
}

/** 範囲削除（範囲・複数ブロックの削除、カット）。`[start, end)` を削除し、キャレットは start。 */
export function deleteRange(text: string, start: number, end: number): EditorTextAndCaret {
  const [lo, hi] = orderedBounds(text, start, end);
  return { caret: lo, text: text.slice(0, lo) + text.slice(hi) };
}

/**
 * Backspace を構造変換として横取りすべきか（＝本文先頭などの境界か）。
 * 本文内での通常削除・単語削除はネイティブ委任するため false を返す。
 */
export function isBackspaceBoundary(text: string, caret: number): boolean {
  if (caret <= 0) return false;
  const { lineStart, lineText } = lineBoundsAt(text, caret);
  const parts = parseMarkdownLineParts(lineText);
  const contentStart = lineStart + parts.rawPrefix.length;
  if (caret === contentStart && (parts.kind === "bullet" || parts.kind === "heading")) {
    return true;
  }
  // plain/blank 行頭（前行あり）での結合も横取り対象。
  return caret === lineStart && lineStart > 0;
}

/** Delete を構造変換として横取りすべきか（＝行末で次行があるか）。 */
export function isDeleteForwardBoundary(text: string, caret: number): boolean {
  if (caret >= text.length) return false;
  const { lineEnd } = lineBoundsAt(text, caret);
  return caret === lineEnd && lineEnd < text.length;
}

//------------------------------------------------------------------------------
// リモートキャレット: 生オフセット → 表示 DOM 位置（canvas-remote-carets-layer 用）
//------------------------------------------------------------------------------

/**
 * 生テキストオフセット `n` を、表示 DOM 上の { 行 index, 行内オフセット } へ写像する。
 * 表示 DOM は行ごとの <div> で改行テキストノードを持たないため、行と行内位置を分けて返す。
 * - surface="editor": 編集中 WYSIWYG DOM（本文のみ・グリフは ::before）。行内オフセット = 本文オフセット。
 * - surface="view": MarkdownTextView（markdownLineTextFor の実テキスト）。箇条書き本文ありは「グリフ + 空白」分を加算、
 *   空箇条書きはグリフのみ（空白なし）、見出し/plain は本文のみ。
 */
export function mapRawOffsetToDisplay(
  text: string,
  n: number,
  surface: CaretSurface
): DisplayCaretPosition {
  const lines = text.split(/\r?\n/);
  let base = 0;
  for (let i = 0; i < lines.length; i += 1) {
    const line = lines[i] ?? "";
    const isLast = i === lines.length - 1;
    if (n <= base + line.length || isLast) {
      const parts = parseMarkdownLineParts(line);
      const contentOffset = clamp(n - base - parts.rawPrefix.length, 0, parts.content.length);
      const lineOffset = surface === "view" ? viewLineOffset(parts, contentOffset) : contentOffset;
      return { lineIndex: i, lineOffset };
    }
    base += line.length + 1;
  }
  return { lineIndex: 0, lineOffset: 0 };
}

/**
 * `el` 内で「表示テキスト先頭から offset 文字目」に対応するテキストノードとノード内オフセットを
 * 返す（リモートキャレット計測 canvas-remote-carets-layer が collapsed Range を張る位置の解決に使う）。
 * 編集中 surface の箇条書きは編集不可マーカー span（`.rc-md-marker`, `• `）を実 DOM に持つが、
 * これは表示専用で mapRawOffsetToDisplay の返す行内オフセットには含めないため、走査から除外する。
 * 非編集 surface（MarkdownTextView）にはマーカー span が無いため影響しない（コンテナ自体が
 * contenteditable="false" でも、除外条件はマーカー span のクラスに限定するため誤除外しない）。
 * テキストノードが 1 つも無い場合は null を返す（呼び出し側は要素先頭へフォールバックする）。
 */
export function resolveCaretTextAnchor(
  el: HTMLElement,
  offset: number
): { node: Text; offset: number } | null {
  const walker = el.ownerDocument.createTreeWalker(el, NodeFilter.SHOW_TEXT, {
    acceptNode: (node) =>
      node.parentElement?.closest(`.${EDITOR_MARKER_CLASS}`)
        ? NodeFilter.FILTER_REJECT
        : NodeFilter.FILTER_ACCEPT,
  });
  let node = walker.nextNode() as Text | null;
  if (!node) return null;
  let remaining = Math.max(0, offset);
  for (;;) {
    const length = node.textContent?.length ?? 0;
    if (remaining <= length) return { node, offset: remaining };
    remaining -= length;
    const next = walker.nextNode() as Text | null;
    if (!next) return { node, offset: length };
    node = next;
  }
}

//------------------------------------------------------------------------------
// Helpers
//------------------------------------------------------------------------------

/**
 * MarkdownTextView の表示テキスト（markdownLineTextFor）上での行内オフセット。
 * 非編集表示は本文を LinkifiedText を通して描画するため、`[label](url)` は `label` へ縮約される。
 * 生本文オフセット（contentOffset）をそのまま使うとリンクを含む行でリモートキャレットが
 * 行末寄りへずれるため、リンク縮約後の表示オフセットへ換算する。
 */
function viewLineOffset(parts: MarkdownLineParts, contentOffset: number): number {
  const displayOffset = linkifiedDisplayOffset(parts.content, contentOffset);
  if (parts.kind === "bullet") {
    // markdownLineTextFor: content ? `${glyph} ${content}` : glyph（空箇条書きは空白なし）。
    // グリフ接頭辞（`• `）はリンクを含まないため、本文側の換算に接頭辞長を足せばよい。
    // 空箇条書きも表示にはグリフ 1 文字が存在するため、本文位置はグリフの後ろ（キャレットが
    // `•` の左へ回り込まないよう displayPrefix = グリフ長）。
    const displayPrefix = parts.content.length > 0 ? parts.glyph.length + 1 : parts.glyph.length;
    return displayPrefix + displayOffset;
  }
  return displayOffset;
}

/**
 * 本文中の生オフセット（rawOffset）を、LinkifiedText が描画する表示テキスト上のオフセットへ
 * 換算する。text / 素 URL セグメントは raw===value で 1:1、表示テキスト付きリンクは
 * segmentDisplayOffset でセグメント内部構造（`[`・ラベル・エスケープ・`](url)`）を考慮して写像する。
 */
function linkifiedDisplayOffset(content: string, rawOffset: number): number {
  let raw = 0;
  let display = 0;
  for (const segment of splitTextIntoLinkSegments(content)) {
    const rawLength = (segment.raw ?? segment.value).length;
    if (rawOffset <= raw + rawLength) {
      return display + segmentDisplayOffset(segment, rawOffset - raw);
    }
    raw += rawLength;
    display += segment.value.length;
  }
  return display;
}

/**
 * セグメント内の生オフセット（rawWithin）を、そのセグメントが描画する表示テキスト上の
 * オフセットへ写像する。text / 素 URL（raw 未設定）は 1:1。表示テキスト付きリンクは
 * `raw = "[" + labelRaw + "](" + url + ")"` の構造を復元し、
 * - 先頭 `[` と `](url)` 以降は表示 0 文字（それぞれ表示先頭 / 表示末尾へ寄せる）
 * - ラベル内はエスケープ（`\[` `\]` `\\` = 2 raw 文字 → 表示 1 文字）を考慮して数える
 * ことで、`[` 直後やエスケープを含むラベルでもリモートキャレットがずれないようにする。
 */
function segmentDisplayOffset(segment: LinkSegment, rawWithin: number): number {
  const clampedValueLength = segment.value.length;
  const clamp = (n: number): number => Math.min(Math.max(n, 0), clampedValueLength);
  // text セグメントと素 URL（raw===value）は表示と生が 1:1。
  if (segment.type !== "link" || segment.raw === undefined) {
    return clamp(rawWithin);
  }
  const raw = segment.raw;
  const url = segment.url ?? "";
  // raw = "[" + labelRaw + "](" + url + ")" より labelRaw 長を復元する（`[`・`]`・`(`・`)` の 4 文字）。
  const labelRawLength = raw.length - url.length - 4;
  if (labelRawLength < 0) {
    // 想定外の構造。安全側で線形フォールバック（旧挙動）。
    return clamp(rawWithin);
  }
  if (rawWithin <= 1) return 0; // 先頭 `[` とその前は表示先頭。
  const labelEnd = 1 + labelRawLength; // ラベル raw の終端（`]` の位置）。
  if (rawWithin >= labelEnd) return clampedValueLength; // `](url)` 以降は表示末尾。
  const labelRaw = raw.slice(1, labelEnd);
  const target = rawWithin - 1; // labelRaw 内の相対生オフセット。
  let consumed = 0;
  let display = 0;
  while (consumed < target && consumed < labelRaw.length) {
    // エスケープ対（`\` + `[`/`]`/`\`）は 2 生文字で表示 1 文字。ラベル文法上、`\` は必ず対で現れる。
    if (labelRaw[consumed] === "\\" && consumed + 1 < labelRaw.length) {
      // 生オフセットがエスケープ対の途中（`\` と対象文字の間）を指す場合は、
      // まだ縮約後の 1 文字を跨いでいない扱いとし、その表示文字の手前へ寄せる。
      if (target === consumed + 1) return clamp(display);
      consumed += 2;
    } else {
      consumed += 1;
    }
    display += 1;
  }
  return clamp(display);
}

/** parts からブロックのスタイルを適用する（markdown-text.ts の markdownLineStyleFor を単一の真実として共有）。 */
function applyLineStyle(div: HTMLElement, parts: MarkdownLineParts): void {
  const style = markdownLineStyleFor(partsToTextLine(parts));
  if (style.fontSize !== undefined) div.style.fontSize = style.fontSize;
  if (style.fontWeight !== undefined) div.style.fontWeight = style.fontWeight;
  if (style.paddingLeft !== undefined) div.style.paddingLeft = style.paddingLeft;
  if (style.textAlign !== undefined) div.style.textAlign = style.textAlign;
  if (style.textIndent !== undefined) div.style.textIndent = style.textIndent;
}

/** MarkdownLineParts を MarkdownTextLine へ変換する（markdownLineStyleFor へ渡すため）。 */
function partsToTextLine(parts: MarkdownLineParts): MarkdownTextLine {
  switch (parts.kind) {
    case "heading":
      // parts.kind==="heading" は level 1〜6 を保証する（parseMarkdownLineParts 内で担保）。
      return { content: parts.content, kind: "heading", level: parts.level as HeadingLevel };
    case "bullet":
      return { content: parts.content, kind: "bullet", level: parts.level };
    case "blank":
      return { kind: "blank" };
    default:
      return { content: parts.content, kind: "plain" };
  }
}

/** ブロック直下のテキストノード数（本文の正準性判定用。マーカー span 内は数えない）。 */
function directTextNodeCount(block: HTMLElement): number {
  let count = 0;
  for (const node of Array.from(block.childNodes)) {
    if (node.nodeType === Node.TEXT_NODE) count += 1;
  }
  return count;
}

/** ブロック内の最初のテキストノードを返す（本文ノード。無ければ null）。マーカー span 等の要素は飛ばす。 */
function firstTextNode(block: HTMLElement): Text | null {
  for (const node of Array.from(block.childNodes)) {
    if (node.nodeType === Node.TEXT_NODE) return node as Text;
  }
  return null;
}

/** ブロックの本文（直下のテキストノードの連結）。マーカー span・<br> 等の要素は含めない。 */
function blockContentText(block: HTMLElement): string {
  let text = "";
  for (const node of Array.from(block.childNodes)) {
    if (node.nodeType === Node.TEXT_NODE) text += node.textContent ?? "";
  }
  return text;
}

/**
 * serialize で信用してよい生プレフィックスを返す。
 * bullet ブロックは自前 build（buildMarkdownEditorDom）が必ず編集不可マーカー span を入れるため、
 * span を欠く bullet は「IME 変換中の Enter がネイティブ委任されたときのブロック分割クローン」
 * （data-* 属性は複製されるがキャレット前方のマーカー span は複製されない）とみなし、
 * data-prefix を信用せず plain 行として扱う。幻の空箇条書きが具現化するより、本文のみの
 * plain 行へ正準化（editorNeedsRebuild が span 欠落を直接検知して再構築）される方が安全。
 * 見出し・plain はマーカー span を持たないのが正準のため、従来どおり data-prefix を用いる。
 */
function trustedBlockPrefix(node: HTMLElement): string {
  if (node.dataset.md === "bullet" && node.querySelector(`.${EDITOR_MARKER_CLASS}`) === null) {
    return "";
  }
  return node.dataset.prefix ?? "";
}

/**
 * ノード + ノード内オフセットを、`el` 先頭からの生テキストオフセットへ変換する。
 * 各ブロックの生行長（prefix 長 + 本文長）を積算し、対象ブロックの prefix 長 + 本文内オフセットを足す。
 */
function rawOffsetOfPoint(el: HTMLElement, node: Node, nodeOffset: number): number | null {
  const blocks = Array.from(el.children).filter((c): c is HTMLElement => c instanceof HTMLElement);
  // 本文長は直下テキストノードの全連結で数える。IME 確定直後はブラウザが既存の空テキストノードの
  // 後ろへ確定テキストを別ノードとして挿入するため、firstTextNode だけでは本文長を取りこぼし、
  // キャレット位置（生オフセット）が本文先頭側へずれて Enter の分割位置等が壊れる。
  // prefix 長は serialize と同じ信頼済み値（trustedBlockPrefix）で数える。マーカー span を欠く
  // bullet の data-prefix をここで加算すると serialize が作る生テキストの座標系とずれ、
  // 再構築前のキャレット保存（reconcileAfterNativeInput）が後方へずれた位置を保存してしまう。
  const rawLenOf = (block: HTMLElement): number =>
    trustedBlockPrefix(block).length + blockContentText(block).length;
  // Range のコンテナが編集要素 root 自身の場合（Ctrl+A の全選択など）は、nodeOffset を子ブロックの
  // index として生オフセットへ写像する（index 0=先頭, index i=ブロック i の直前, 末尾=全長）。
  if (node === el) {
    if (nodeOffset <= 0) return 0;
    let acc = 0;
    for (let i = 0; i < blocks.length; i += 1) {
      if (i === nodeOffset) return acc;
      const block = blocks[i];
      if (block) acc += rawLenOf(block) + 1;
    }
    // 末尾（全ブロック選択の終端）。末尾に付いた余分な改行 1 文字ぶんを引いて全長へ。
    return Math.max(0, acc - 1);
  }
  let base = 0;
  for (const block of blocks) {
    const prefixLen = trustedBlockPrefix(block).length;
    const contentLen = blockContentText(block).length;
    if (block === node || block.contains(node)) {
      const contentOffset = contentOffsetWithinBlock(block, node, nodeOffset, contentLen);
      return base + prefixLen + contentOffset;
    }
    base += prefixLen + contentLen + 1; // 改行 1 文字ぶん。
  }
  return null;
}

/** ブロック内での本文オフセットを求める（本文テキストノード基準。要素境界は先頭/末尾へ寄せる）。 */
function contentOffsetWithinBlock(
  block: HTMLElement,
  node: Node,
  nodeOffset: number,
  contentLen: number
): number {
  // 本文は直下テキストノードの連結。IME 確定直後は複数テキストノード（空ノード + 確定ノード）に
  // 分かれ得るため、対象ノードより前にある直下テキストノードの長さを積算して本文オフセットへ換算する。
  // 正準（単一ノード）ブロックでは従来（firstTextNode 基準の clamp）と完全一致する。
  if (node.nodeType === Node.TEXT_NODE && node.parentNode === block) {
    let preceding = 0;
    for (const child of Array.from(block.childNodes)) {
      if (child === node) {
        return clamp(preceding + nodeOffset, 0, contentLen);
      }
      if (child.nodeType === Node.TEXT_NODE) preceding += child.textContent?.length ?? 0;
    }
    return contentLen;
  }
  // node がブロック要素自身（子 index）や <br> の場合は、子 index から前後どちらへ寄せるか決める。
  if (node === block) {
    // 子が 0 個 / index 0 の位置は本文先頭、それ以外は本文末尾へ寄せる。
    return nodeOffset <= 0 ? 0 : contentLen;
  }
  // それ以外（マーカー span 内などの想定外ノード）は本文末尾へ寄せる。
  return contentLen;
}

/** offset を含む行の範囲と行文字列を返す（改行を含まない 1 行）。 */
function lineBoundsAt(
  text: string,
  offset: number
): { lineStart: number; lineEnd: number; lineText: string } {
  const clamped = clamp(offset, 0, text.length);
  const lineStart = text.lastIndexOf("\n", clamped - 1) + 1;
  const nextNewline = text.indexOf("\n", clamped);
  const lineEnd = nextNewline === -1 ? text.length : nextNewline;
  return { lineEnd, lineStart, lineText: text.slice(lineStart, lineEnd) };
}

/** start/end を [0,len] にクランプし昇順で返す。 */
function orderedBounds(text: string, start: number, end: number): [number, number] {
  const a = clamp(start, 0, text.length);
  const b = clamp(end, 0, text.length);
  return a <= b ? [a, b] : [b, a];
}

function clamp(value: number, min: number, max: number): number {
  return Math.max(min, Math.min(max, value));
}
